import React from 'react';
import IconFont from '$/components/IconFont';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { handleImageUrl } from '$/utils/image';
import {
  type RoleGenStatus,
  type ThreeViewImage,
} from './useRoleGenSubmit';
import styles from './index.module.scss';

interface ThreeViewPreviewProps {
  images: ThreeViewImage[];
  status: RoleGenStatus;
  /** 存为资产回调 */
  onSaveAsAsset: (image: ThreeViewImage) => void;
  /** 添加到当前画布回调；非画布入口不传。 */
  onAddToCanvas?: (image: ThreeViewImage) => void;
  /** 团队画布不可编辑时禁用添加入口并展示原因。 */
  addToCanvasDisabledReason?: string;
}

/**
 * 角色预览区；生成完成后按右侧视觉稿展示首张结果图和保存入口。
 *
 * @param props 见 ThreeViewPreviewProps
 * @returns 预览节点
 */
function ThreeViewPreview({
  images,
  status,
  onSaveAsAsset,
  onAddToCanvas,
  addToCanvasDisabledReason,
}: ThreeViewPreviewProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isOversea = useOfficialStore((state) => state.isOversea());

  if (status === 'generating') {
    return (
      <div className={styles.previewStage}>
        <div className={styles.generatingPreview}>
          <div className={styles.emptyPreviewIconWrap}>
            <IconFont
              name="fourPointedStarFill"
              fontSize={20}
              color="currentColor"
              className={styles.gradientStarIcon}
            />
          </div>
          <span className={styles.generatingText}>
            {`${languageStore.smartcatTranslationText((value) => value.generating)}...`}
          </span>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className={styles.previewStage}>
        <div className={styles.emptyPreview}>
          <div className={styles.emptyPreviewIconWrap}>
            <IconFont
              name="fourPointedStarFill"
              fontSize={20}
              color="currentColor"
              className={styles.gradientStarIcon}
            />
          </div>
          <div className={styles.emptyPreviewText}>
            <span>{languageStore.smartcatTranslationText((value) => value.after_completing_the_character_setting)}</span>
            <span>{languageStore.smartcatTranslationText((value) => value.generate_character_orthographic_views)}</span>
          </div>
        </div>
      </div>
    );
  }

  const firstImage = images[0];
  const displayUrl = firstImage.originalUrl;

  return (
    <div className={`${styles.previewStage} ${styles.previewStageGenerated}`}>
      <div className={styles.previewGenerated}>
        <div className={styles.previewImageCard}>
          {displayUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className={styles.previewImage}
              src={handleImageUrl(displayUrl, {
                showWatermark: !isOversea,
              })}
              alt=""
              crossOrigin="anonymous"
            />
          ) : (
            <div className={styles.previewImagePlaceholder}>
              <IconFont
                name="fourPointedStarFill"
                fontSize={24}
                color="currentColor"
                className={styles.gradientStarIcon}
              />
            </div>
          )}
        </div>
        <div className={styles.previewActions}>
          <button
            type="button"
            className={styles.savePreviewButton}
            onClick={() => onSaveAsAsset(firstImage)}
          >
            {languageStore.smartcatTranslationText((value) => value.save_to_asset_library)}
          </button>
          {onAddToCanvas && (
            <button
              type="button"
              className={styles.savePreviewButton}
              disabled={Boolean(addToCanvasDisabledReason)}
              title={addToCanvasDisabledReason || undefined}
              onClick={() => onAddToCanvas(firstImage)}
            >
              {languageStore.smartcatTranslationText((value) => value.add_to_canvas)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ThreeViewPreview;
